import { FC, useState, useRef, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { FileUpload, FileUploadHandlerEvent } from 'primereact/fileupload';
import { ProgressSpinner } from 'primereact/progressspinner';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { WaterfallViewModel, WaterfallExportSelection } from '../../models/waterfall';

type MigrationMode = null | 'export' | 'import';

interface WaterfallMigrationDialogProps {
  visible: boolean;
  onClose: () => void;
  onExport: (selections: WaterfallExportSelection[]) => Promise<void>;
  onImport: (file: File) => Promise<void>;
  waterfalls: WaterfallViewModel[];
}

interface ExportRow {
  waterfallId: number;
  name: string;
  integrationType: string;
  selectedVersionId: number;
  versions: { label: string; value: number }[];
  checked: boolean;
}

export const WaterfallMigrationDialog: FC<WaterfallMigrationDialogProps> = ({
  visible,
  onClose,
  onExport,
  onImport,
  waterfalls,
}) => {
  const [mode, setMode] = useState<MigrationMode>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileUploadRef = useRef<FileUpload>(null);

  const initialExportRows = useMemo((): ExportRow[] => {
    return waterfalls.map((w) => {
      const versionOptions = (w.versions ?? []).map((v) => ({
        label: `v${v.versionNumber}${v.waterfallVersionId === w.currentVersionId ? ' (active)' : ''}`,
        value: v.waterfallVersionId,
      }));
      const activeVersionId = w.currentVersionId ?? versionOptions[0]?.value ?? 0;
      return {
        waterfallId: w.waterfallId,
        name: w.name,
        integrationType: w.integrationType,
        selectedVersionId: activeVersionId,
        versions: versionOptions,
        checked: true,
      };
    });
  }, [waterfalls]);

  const [exportRows, setExportRows] = useState<ExportRow[]>(initialExportRows);

  // Reset export rows when dialog opens
  const handleOpen = () => {
    setExportRows(initialExportRows);
  };

  const handleClose = () => {
    if (isProcessing) return;
    setMode(null);
    setSelectedFile(null);
    setError(null);
    onClose();
  };

  const allChecked = exportRows.length > 0 && exportRows.every((r) => r.checked);
  const someChecked = exportRows.some((r) => r.checked);

  const toggleAll = () => {
    const newChecked = !allChecked;
    setExportRows((prev) => prev.map((r) => ({ ...r, checked: newChecked })));
  };

  const toggleRow = (waterfallId: number) => {
    setExportRows((prev) =>
      prev.map((r) => (r.waterfallId === waterfallId ? { ...r, checked: !r.checked } : r)),
    );
  };

  const setRowVersion = (waterfallId: number, versionId: number) => {
    setExportRows((prev) =>
      prev.map((r) => (r.waterfallId === waterfallId ? { ...r, selectedVersionId: versionId } : r)),
    );
  };

  const handleFinish = async () => {
    setError(null);
    setIsProcessing(true);

    try {
      if (mode === 'export') {
        const selections: WaterfallExportSelection[] = exportRows
          .filter((r) => r.checked)
          .map((r) => ({ waterfallId: r.waterfallId, waterfallVersionId: r.selectedVersionId }));
        await onExport(selections);
        handleClose();
      } else if (mode === 'import' && selectedFile) {
        await onImport(selectedFile);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileSelect = (e: FileUploadHandlerEvent) => {
    if (e.files && e.files.length > 0) {
      setSelectedFile(e.files[0]);
      setError(null);
    }
  };

  const handleBack = () => {
    setMode(null);
    setSelectedFile(null);
    setError(null);
    fileUploadRef.current?.clear();
  };

  const canFinish =
    (mode === 'export' && someChecked) || (mode === 'import' && selectedFile !== null);

  const footer = (
    <div className="flex justify-end gap-2">
      {mode !== null && (
        <Button
          label="Back"
          icon="pi pi-arrow-left"
          className="p-button-secondary"
          onClick={handleBack}
          disabled={isProcessing}
        />
      )}
      <Button
        label="Cancel"
        className="p-button-secondary"
        onClick={handleClose}
        disabled={isProcessing}
      />
      {mode !== null && (
        <Button
          label={mode === 'export' ? 'Export Selected' : 'Prepare Import'}
          icon={mode === 'export' ? 'pi pi-download' : 'pi pi-upload'}
          className="p-button-primary"
          onClick={handleFinish}
          disabled={!canFinish || isProcessing}
          loading={isProcessing}
        />
      )}
    </div>
  );

  return (
    <Dialog
      header="Waterfall Migration"
      visible={visible}
      onHide={handleClose}
      onShow={handleOpen}
      footer={footer}
      style={{ width: mode === 'export' ? '720px' : '520px' }}
      dismissableMask={!isProcessing}
      closable={!isProcessing}
    >
      {isProcessing && (
        <div className="flex flex-column align-items-center justify-content-center gap-2 py-4">
          <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" animationDuration=".5s" />
          <span className="text-sm text-gray-600">
            {mode === 'export' ? 'Exporting...' : 'Preparing import...'}
          </span>
        </div>
      )}

      {!isProcessing && mode === null && (
        <div className="flex flex-column gap-3">
          <p className="m-0 text-sm text-gray-600">
            Choose an action to migrate waterfall configurations between environments.
          </p>
          <div className="flex gap-3">
            <Button
              label="Export"
              icon="pi pi-download"
              className="p-button-outlined flex-1"
              style={{ height: '80px' }}
              onClick={() => setMode('export')}
            />
            <Button
              label="Import"
              icon="pi pi-upload"
              className="p-button-outlined flex-1"
              style={{ height: '80px' }}
              onClick={() => setMode('import')}
            />
          </div>
        </div>
      )}

      {!isProcessing && mode === 'export' && (
        <div className="flex flex-column gap-3">
          <div className="bg-blue-50 p-3 border-round">
            <div className="flex align-items-center gap-2">
              <i className="pi pi-info-circle text-blue-600" />
              <span className="text-sm">
                Select the waterfalls and versions to export. Each waterfall exports a single version.
              </span>
            </div>
          </div>
          <DataTable
            value={exportRows}
            className="p-datatable-sm p-datatable-gridlines"
            scrollable
            scrollHeight="400px"
            emptyMessage="No waterfalls available"
          >
            <Column
              header={
                <Checkbox
                  checked={allChecked}
                  onChange={toggleAll}
                />
              }
              style={{ width: '3rem' }}
              body={(row: ExportRow) => (
                <Checkbox
                  checked={row.checked}
                  onChange={() => toggleRow(row.waterfallId)}
                />
              )}
            />
            <Column field="name" header="Waterfall" />
            <Column field="integrationType" header="Type" style={{ width: '10rem' }} />
            <Column
              header="Version"
              style={{ width: '12rem' }}
              body={(row: ExportRow) => (
                <Dropdown
                  value={row.selectedVersionId}
                  options={row.versions}
                  onChange={(e) => setRowVersion(row.waterfallId, e.value)}
                  disabled={!row.checked}
                  className="w-full p-inputtext-sm"
                />
              )}
            />
          </DataTable>
          <div className="text-sm text-gray-500">
            {exportRows.filter((r) => r.checked).length} of {exportRows.length} waterfall(s) selected
          </div>
        </div>
      )}

      {!isProcessing && mode === 'import' && (
        <div className="flex flex-column gap-3">
          <div className="bg-blue-50 p-3 border-round">
            <div className="flex align-items-center gap-2">
              <i className="pi pi-info-circle text-blue-600" />
              <span className="text-sm">
                Upload a previously exported migration JSON file to compare against the current waterfalls.
              </span>
            </div>
          </div>
          <FileUpload
            ref={fileUploadRef}
            mode="advanced"
            accept=".json"
            maxFileSize={10000000}
            customUpload
            uploadHandler={handleFileSelect}
            auto
            chooseLabel="Choose JSON File"
            className="w-full"
            emptyTemplate={
              <div className="flex align-items-center flex-column py-3">
                <i className="pi pi-file text-4xl text-gray-400 mb-2" />
                <span className="text-sm text-gray-500">Drag and drop a .json file here</span>
              </div>
            }
          />
          {selectedFile && (
            <div className="flex align-items-center gap-2 p-2 surface-100 border-round">
              <i className="pi pi-file text-green-600" />
              <span className="text-sm font-semibold">{selectedFile.name}</span>
              <span className="text-sm text-gray-500">({(selectedFile.size / 1024).toFixed(1)} KB)</span>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mt-3 p-3 bg-red-50 border-round">
          <div className="flex align-items-center gap-2">
            <i className="pi pi-exclamation-triangle text-red-600" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        </div>
      )}
    </Dialog>
  );
};
